import type { AwsPlatformIdentity } from '../../api/client'
import { IamSetupTabs } from './IamSetupTabs'
import { MANAGED_POLICY_ARNS, READONLY_NAME, waToolCliStep } from './iamSetup'

/**
 * Setup help for the cross-account assume-role method — the snippets the
 * CUSTOMER runs in THEIR account.
 *
 * The other two methods end with a secret changing hands. This one doesn't: the
 * customer creates a read-only role that trusts our account id, REQUIRES this
 * company's platform-generated ExternalId (the confused-deputy guard — another
 * tenant naming the same role ARN is refused), and sends back only the role
 * ARN. We assume it from the platform's own credentials.
 *
 * The trust policy has to name a concrete account and ExternalId, so this
 * component renders no snippet until both resolve — a template with a blank
 * Principal or a placeholder ExternalId would set the customer up to fail.
 */

const ROLE_NAME = READONLY_NAME

const policyList = (indent: string) => MANAGED_POLICY_ARNS.map((a) => `${indent}- ${a}`).join('\n')
const policyHcl = MANAGED_POLICY_ARNS.map((a) => `    "${a}",`).join('\n')

function cloudformation(accountId: string, externalId: string): string {
  return `AWSTemplateFormatVersion: '2010-09-09'
Description: Read-only role for the AllCode AI Assessment scan (cross-account)
Resources:
  AssessmentReadOnlyRole:
    Type: AWS::IAM::Role
    Properties:
      RoleName: ${ROLE_NAME}
      AssumeRolePolicyDocument:
        Version: '2012-10-17'
        Statement:
          - Effect: Allow
            Principal:
              AWS: arn:aws:iam::${accountId}:root
            Action: sts:AssumeRole
            Condition:
              StringEquals:
                sts:ExternalId: '${externalId}'
      ManagedPolicyArns:
${policyList('        ')}
Outputs:
  RoleArn:
    Description: Send this ARN back to AllCode
    Value: !GetAtt AssessmentReadOnlyRole.Arn`
}

function terraform(accountId: string, externalId: string): string {
  return `resource "aws_iam_role" "assessment_readonly" {
  name = "${ROLE_NAME}"

  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect    = "Allow"
      Principal = { AWS = "arn:aws:iam::${accountId}:root" }
      Action    = "sts:AssumeRole"
      Condition = {
        StringEquals = { "sts:ExternalId" = "${externalId}" }
      }
    }]
  })

  managed_policy_arns = [
${policyHcl}
  ]
}

output "role_arn" {
  description = "Send this ARN back to AllCode"
  value       = aws_iam_role.assessment_readonly.arn
}`
}

function cli(accountId: string, externalId: string): string {
  return `# Run in the account you want scanned.
cat > trust.json <<'JSON'
{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Principal": { "AWS": "arn:aws:iam::${accountId}:root" },
    "Action": "sts:AssumeRole",
    "Condition": { "StringEquals": { "sts:ExternalId": "${externalId}" } }
  }]
}
JSON

aws iam create-role --role-name ${ROLE_NAME} \\
  --assume-role-policy-document file://trust.json

for p in \\
${MANAGED_POLICY_ARNS.map((a) => `  ${a}`).join(' \\\n')} ; do
  aws iam attach-role-policy --role-name ${ROLE_NAME} --policy-arn "$p"
done

${waToolCliStep('role', ROLE_NAME)}

# Send this back to AllCode:
aws iam get-role --role-name ${ROLE_NAME} --query 'Role.Arn' --output text`
}

function cloudshell(accountId: string, externalId: string): string {
  return `# Paste into AWS CloudShell in the account you want scanned.
aws iam create-role --role-name ${ROLE_NAME} --assume-role-policy-document '{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Principal": { "AWS": "arn:aws:iam::${accountId}:root" },
    "Action": "sts:AssumeRole",
    "Condition": { "StringEquals": { "sts:ExternalId": "${externalId}" } }
  }]
}' >/dev/null

for p in ${MANAGED_POLICY_ARNS.join(' ')}; do
  aws iam attach-role-policy --role-name ${ROLE_NAME} --policy-arn "$p"
done
${waToolCliStep('role', ROLE_NAME)}

echo "Role ARN to send to AllCode:"
aws iam get-role --role-name ${ROLE_NAME} --query 'Role.Arn' --output text`
}

export function AwsAssumeRoleSetupHelp({ identity }: { identity: AwsPlatformIdentity | null }) {
  const accountId = identity?.account_id
  const ext = identity?.external_id

  if (identity === null) {
    return (
      <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }} data-testid="aws-assume-role-help-loading">
        Reading our AWS identity…
      </p>
    )
  }

  if (!accountId || !ext) {
    // Without our account id the trust policy has no principal, so there is no
    // honest snippet to show.
    return (
      <div className="notice notice--warn" data-testid="aws-assume-role-help-unavailable">
        <p style={{ margin: 0 }}>
          The customer's role must trust <strong>our</strong> AWS account and require this
          company's External ID, but this deployment can't read them, so the setup snippet
          can't be generated.
          {identity.reason ? ` (${identity.reason})` : ''} Check the platform's AWS
          credentials in admin settings, or use a different connection method.
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="notice" data-testid="aws-assume-role-help-summary">
        <p style={{ margin: 0 }}>
          No keys change hands. The customer creates a read-only role in their account that
          trusts <strong>{accountId}</strong> (ours), and sends back the role ARN. We assume it
          for each scan and get temporary, auto-expiring credentials.
        </p>
      </div>
      <IamSetupTabs
        triggerLabel="Show the customer the role setup →"
        modalTitle="Read-only cross-account role"
        testIdPrefix="aws-assume-role-help"
        intro={
          <>
            <p>
              Send this to the customer to run in the account they want scanned. It creates a role
              named <code>{ROLE_NAME}</code> with AWS's read-only managed policies, trusting our
              account <code>{accountId}</code> and requiring this company's External ID
              (<code data-testid="aws-assume-role-help-external-id">{ext}</code>). The ID is issued by
              the platform — don't change it; a role that can be assumed without it is rejected.
            </p>
          </>
        }
        footer={
          <p style={{ color: 'var(--muted)' }}>
            The template outputs the role ARN — paste it into the Role ARN field, then use
            <strong> Test role</strong> to confirm the trust policy admits us.
          </p>
        }
        snippets={{
          cloudformation: cloudformation(accountId, ext),
          terraform: terraform(accountId, ext),
          cli: cli(accountId, ext),
          cloudshell: cloudshell(accountId, ext),
        }}
      />
    </>
  )
}

